import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import ModalContainer from '@components/modals/ModalContainer';
import { toastError } from '@components/Toast';

import SendMailToBuyer from '@features/collection/components/SendMailToBuyer';
import { acceptFileType } from '@features/crm/utils/helpers';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import {
	addPresentationSequence,
	approvedCollection,
	fetchPresentationDefaultCoverImages,
	getPresentationSequence,
	removePresentationSequenceDoc
} from '@services/collectionService';
import { uploadDocument } from '@services/commonService';

import { isValidFile, sliderSettings } from '@utils/Util';

import { VALID_FILE_SIZE } from '@/constants/constant';

const UploadPresentationImages = ({
	onClose,
	title,
	callBack,
	collectionInfo,
	isEdit,
	shareCollection,
	isApprovedRequired = false,
	entityType = 'COLLECTION',
	presentationId
}) => {
	const [isShowSendMailModal, setIsShowSendMailModal] = useState(false);
	const [defaultCoverImages, setDefaultCoverImages] = useState([]);
	const [midCoverImages, setMidCoverImages] = useState([]);
	const [lastCoverImages, setLastCoverImages] = useState([]);
	const [selectedImages, setSelectedImages] = useState({
		defaultCoverImages: null,
		midCoverImages: null,
		lastCoverImages: null
	});
	const [errors, setErrors] = useState({});
	const [inputData, setInputData] = useState({});
	const dispatch = useDispatch();

	const fetchDefaultImages = async () => {
		try {
			dispatch(openLoader());
			const data = await fetchPresentationDefaultCoverImages();
			setDefaultCoverImages(data);
			if (!isEdit && data.length > 0) {
				setSelectedImages((prev) => ({ ...prev, defaultCoverImages: data[0] }));
			}
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const fetchPresentationInfo = async () => {
		try {
			dispatch(openLoader());
			const data = await getPresentationSequence(presentationId);
			setInputData({
				brand: data.brand,
				market: data.market,
				season: data.season,
				presentationName: data?.presentationName
			});

			let defaultImages = data.documentList.filter((item) => {
				if (item.docType === 'PRESENTATION_COVER_IMAGE') {
					item.isDeletable = true;
					return true;
				}
				if (item.docType === 'STATIC_DOCUMENT') {
					item.isDeletable = false;
					return true;
				}
			});
			let midImages = data.documentList.filter((item) => {
				if (item.docType === 'PRESENTATION_MIDDLE_IMAGE') {
					item.isDeletable = true;
					return true;
				}
			});
			let endImages = data.documentList.filter((item) => {
				if (item.docType === 'PRESENTATION_END_IMAGE') {
					item.isDeletable = true;
					return true;
				}
			});

			setSelectedImages({
				defaultCoverImages: defaultImages?.find((item) => item.id === data.coverDocumentId) || null,
				midCoverImages: midImages?.find((item) => item.id === data.middleDocumentId) || null,
				lastCoverImages: endImages?.find((item) => item.id === data.endDocumentId) || null
			});

			setDefaultCoverImages((prev) => [...prev, ...defaultImages]);
			setMidCoverImages(midImages);
			setLastCoverImages(endImages);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		if (isEdit) {
			fetchPresentationInfo();
		} else {
			setInputData(collectionInfo);
		}
		fetchDefaultImages();
	}, []);

	const handleChange = (e) => {
		let { name, value } = e.target;
		setInputData((prev) => ({
			...prev,
			[name]: value
		}));
	};

	const isValidInputData = () => {
		let errorObj = {};
		if (inputData?.brand && inputData.brand.length > 14) {
			errorObj['brandError'] = 'Text limit 14';
		}

		setErrors(errorObj);
		return Object.keys(errorObj).length === 0;
	};

	const getDocumentIds = () => {
		const defaultImageIds = defaultCoverImages
			.filter((item) => item?.isDeletable === true)
			.map((item) => item.id);
		const midImageIds = midCoverImages
			.filter((item) => item?.isDeletable === true)
			.map((item) => item.id);
		const lastImageIds = lastCoverImages
			.filter((item) => item?.isDeletable === true)
			.map((item) => item.id);
		let idList = [...defaultImageIds, ...midImageIds, ...lastImageIds];
		if (
			selectedImages.defaultCoverImages !== null &&
			!idList.includes(selectedImages.defaultCoverImages.id)
		) {
			idList.push(selectedImages.defaultCoverImages.id);
		}
		return idList;
	};

	const handleSubmit = async () => {
		if (isValidInputData()) {
			try {
				dispatch(openLoader());
				let postData = {
					entityId: collectionInfo.collectionId,
					entityType: entityType,
					brand: inputData.brand,
					market: inputData.market,
					season: inputData.season,
					coverDocumentId: selectedImages.defaultCoverImages?.id || null,
					middleDocumentId: selectedImages.midCoverImages?.id || null,
					endDocumentId: selectedImages.lastCoverImages?.id || null,
					documentIdList: getDocumentIds(),
					presentationName: inputData?.presentationName
				};
				await addPresentationSequence(postData, presentationId);
				if (shareCollection) await shareCollection();
				if (isApprovedRequired) await approvedCollection(collectionInfo.collectionId);
				if (callBack) await callBack();
				dispatch(closeLoader());
				if (!isEdit) setIsShowSendMailModal(true);
				if (isEdit) onClose();
			} catch {
				dispatch(closeLoader());
			}
		}
	};

	const toggleSelectedImage = (item, fieldName) => {
		const isExist = selectedImages[fieldName]?.id === item.id;
		setSelectedImages((prev) => ({ ...prev, [fieldName]: isExist ? null : item }));
	};

	const removeImage = async (item) => {
		try {
			dispatch(openLoader());
			await removePresentationSequenceDoc({
				entityId: collectionInfo.collectionId,
				entityType: entityType,
				documentType: item.docType,
				documentId: item.id,
				isRootImage: !!selectedImages[item.docType]?.id
			});
			if (item.docType === 'PRESENTATION_COVER_IMAGE' || item.docType === 'STATIC_DOCUMENT') {
				setDefaultCoverImages((prev) => prev.filter((obj) => obj.id !== item.id));
			}
			if (item.docType === 'PRESENTATION_MIDDLE_IMAGE') {
				setMidCoverImages((prev) => prev.filter((obj) => obj.id !== item.id));
			}
			if (item.docType === 'PRESENTATION_END_IMAGE') {
				setLastCoverImages((prev) => prev.filter((obj) => obj.id !== item.id));
			}
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const getUniqueObjectsById = (inputArray) => {
		const uniqueObjects = {};

		for (const obj of inputArray) {
			uniqueObjects[obj.id] = obj;
		}
		const uniqueArray = Object.values(uniqueObjects);
		uniqueArray.sort((a, b) => b.id - a.id);

		return uniqueArray;
	};

	const renderCoverPhotos = (images, fieldName) => {
		return getUniqueObjectsById(images)?.map((item) => {
			return (
				<div
					className={`single-item ${selectedImages[fieldName]?.id === item.id ? 'selected' : ''}`}
					key={`cover_photo_${item.id}`}
					onClick={() => toggleSelectedImage(item, fieldName)}
				>
					<img src={item.docUrl} alt="cover" className="cover-photo" />
					{item.isDeletable && (
						<img
							src={`${process.env.PUBLIC_URL}/icons/delete_gray.svg`}
							alt="delete"
							className="action delete"
							onClick={() => removeImage(item)}
						/>
					)}
				</div>
			);
		});
	};

	const onUploadImage = async (postData, type) => {
		try {
			dispatch(openLoader());
			const data = await uploadDocument(postData);
			if (type === 'PRESENTATION_COVER_IMAGE') {
				let cloneData = [{ ...data, isDeletable: true }, ...defaultCoverImages];
				setDefaultCoverImages(getUniqueObjectsById(cloneData));
			}
			if (type === 'PRESENTATION_MIDDLE_IMAGE') {
				setMidCoverImages((prev) => [{ ...data, isDeletable: true }, ...prev]);
			}
			if (type === 'PRESENTATION_END_IMAGE') {
				setLastCoverImages((prev) => [{ ...data, isDeletable: true }, ...prev]);
			}
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const onFileUpload = (e, docType) => {
		let file = e.target.files[0];
		if (isValidFile(file, 'IMAGE', VALID_FILE_SIZE)) {
			let data = {
				name: file.name,
				docMimeType: file.type,
				documentType: docType,
				base64Str: ''
			};
			let reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = async () => {
				data.base64Str = reader.result;
				await onUploadImage(data, docType);
			};
			reader.onerror = function (error) {
				toastError(error);
			};
		}
	};

	return (
		<>
			{!isShowSendMailModal && (
				<ModalContainer onCloseModal={onClose}>
					<div className="common-popup presentation-settings-modal shadow-24dp">
						<div className="common-popup-header d-flex justify-content-between">
							<div className="popup-tilte">
								<h3 className="semibold-16 mb-0">{title}</h3>
							</div>
							<div className="close-btn" onClick={onClose}>
								<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
							</div>
						</div>
						<div className="common-popup-body">
							<div className="container px-4">
								<div className="row">
									<div className="col-md-12">
										<div className="form-group">
											<label htmlFor="brandName">Presentation name</label>
											<input
												type="text"
												autoComplete="off"
												placeholder="Brand name"
												name="presentationName"
												value={inputData?.presentationName || ''}
												onChange={handleChange}
												className={errors.presentationNameError ? 'required' : ''}
											/>
											{errors?.presentationNameError && (
												<span className="error">{errors.presentationNameError}</span>
											)}
										</div>
									</div>

									<div className="col-md-12">
										<div className="form-group">
											<label htmlFor="brandName">Brand name (Text limit 14)</label>
											<input
												type="text"
												autoComplete="off"
												placeholder="Brand name"
												name="brand"
												value={inputData?.brand || ''}
												onChange={handleChange}
												className={errors.brandError ? 'required' : ''}
											/>
											{errors?.brandError && (
												<span className="error">{errors.brandError}</span>
											)}
										</div>
									</div>
									<div className="col-md-6 pr-6">
										<div className="form-group">
											<label htmlFor="market">Department</label>
											<input
												type="text"
												autoComplete="off"
												name="market"
												placeholder="Type department"
												value={inputData?.market || ''}
												onChange={handleChange}
											/>
										</div>
									</div>
									<div className="col-md-6 pl-6">
										<div className="form-group">
											<label htmlFor="season">Season</label>
											<input
												type="text"
												autoComplete="off"
												name="season"
												placeholder="Type season"
												value={inputData?.season || ''}
												onChange={handleChange}
											/>
										</div>
									</div>
								</div>
							</div>
							<div className="cover-photos pl-4">
								<p className="regular-12 gray_dark_01 weight-500 mb-2">
									Select cover photo or upload{' '}
								</p>
								<div className="upload-cover-photos">
									<div className="upload-item">
										<div className="file-uploader">
											<label htmlFor="drag-upload1" className="drag-upload" />
											<input
												type="file"
												id="drag-upload1"
												className="file-upload"
												name="logo"
												onClick={(e) => (e.target.value = null)}
												onChange={(e) => onFileUpload(e, 'PRESENTATION_COVER_IMAGE')}
												accept={acceptFileType('image')}
												multiple={false}
											/>
										</div>
									</div>
									<div className="uploaded-items">
										<div className="cover-slider">
											<Slider {...sliderSettings}>
												{renderCoverPhotos(defaultCoverImages, 'defaultCoverImages')}
											</Slider>
										</div>
									</div>
								</div>
							</div>

							<div className="upload-pages pl-4 mt-2">
								<p className="regular-12 gray_dark_01 weight-500 mb-2">
									Upload page:{' '}
									<span className="gray_dark_02">
										It will be placed after the cover photo
									</span>
								</p>
								<div className="upload-cover-photos">
									<div className="upload-item">
										<div className="file-uploader">
											<label htmlFor="drag-upload2" className="drag-upload" />
											<input
												type="file"
												id="drag-upload2"
												className="file-upload"
												name="logo"
												onClick={(e) => (e.target.value = null)}
												onChange={(e) => onFileUpload(e, 'PRESENTATION_MIDDLE_IMAGE')}
												accept={acceptFileType('image')}
												multiple={false}
											/>
										</div>
									</div>
									<div className="uploaded-items">
										<div className="cover-slider">
											<Slider {...sliderSettings}>
												{renderCoverPhotos(midCoverImages, 'midCoverImages')}
											</Slider>
										</div>
									</div>
								</div>
							</div>

							<div className="upload-pages pl-4 mt-2">
								<p className="regular-12 gray_dark_01 weight-500 mb-2">
									Upload page:{' '}
									<span className="gray_dark_02">
										It will be placed before the end page
									</span>
								</p>
								<div className="upload-cover-photos">
									<div className="upload-item">
										<div className="file-uploader">
											<label htmlFor="drag-upload3" className="drag-upload" />
											<input
												type="file"
												id="drag-upload3"
												className="file-upload"
												name="logo"
												onClick={(e) => (e.target.value = null)}
												onChange={(e) => onFileUpload(e, 'PRESENTATION_END_IMAGE')}
												accept={acceptFileType('image')}
												multiple={false}
											/>
										</div>
									</div>
									<div className="uploaded-items">
										<div className="cover-slider">
											<Slider {...sliderSettings}>
												{renderCoverPhotos(lastCoverImages, 'lastCoverImages')}
											</Slider>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className="modal-footer">
							<div className="footer-buttons text-right">
								<button className="button gray-outline size32" onClick={onClose}>
									Cancel
								</button>
								<button className="button size32 ml-2" onClick={handleSubmit}>
									Submit
								</button>
							</div>
						</div>
					</div>
				</ModalContainer>
			)}
			{isShowSendMailModal && (
				<SendMailToBuyer
					isOpenModal={isShowSendMailModal}
					onClose={() => {
						setIsShowSendMailModal(false);
						onClose();
					}}
					brandName={collectionInfo.brand}
					collectionId={collectionInfo.collectionId}
					entityType={entityType}
					presentationId={presentationId}
				/>
			)}
		</>
	);
};

export default UploadPresentationImages;
